import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import type {
  Location,
  PurchaseOrderCompanyLocationListQuery,
  PurchaseOrderCompanyLocationListQueryVariables,
  WarehouseAreaQueryListArgs,
  WarehouseListQuery,
} from "@/graphql/codegen/graphql"
import {
  CompanyStatus,
  PurchaseOrderCompanyLocationListDocument,
  WarehouseListDocument,
} from "@/graphql/codegen/graphql"
import { CompanyType } from "@/types/companyType"
import { deduplicateLocations, sortIncotermsLocations } from "@/utils/locations"

interface UseGetPurchaseOrderIncotermsLocationsReturn {
  locations: Location[]
  isFetchingLocations: boolean
  loadMoreLocations: () => void
}

const LIMIT = 100

export const useGetPurchaseOrderIncotermsLocations = (): UseGetPurchaseOrderIncotermsLocationsReturn => {
  const [rawLocations, setRawLocations] = useState<Location[]>([])
  const [isFetchingLocations, setIsFetchingLocations] = useState(true)

  const [warehouseOffset, setWarehouseOffset] = useState(0)
  const [isWarehouseLastPage, setIsWarehouseLastPage] = useState(false)

  const [intermediaryOffset, setIntermediaryOffset] = useState(0)
  const [isIntermediaryLastPage, setIsIntermediaryLastPage] = useState(false)

  const [{ data: warehouse }] = useQuery<WarehouseListQuery, WarehouseAreaQueryListArgs>({
    query: WarehouseListDocument,
    variables: {
      input: {
        filter: {
          isActive: true,
        },
        limit: LIMIT,
        offset: warehouseOffset,
      },
    },
    pause: isWarehouseLastPage,
  })

  const [{ data: intermediaryCompaniesData }] = useQuery<
    PurchaseOrderCompanyLocationListQuery,
    PurchaseOrderCompanyLocationListQueryVariables
  >({
    query: PurchaseOrderCompanyLocationListDocument,
    variables: {
      input: {
        filter: {
          types: [CompanyType.IntermediaryDestination],
          statuses: [CompanyStatus.Active],
        },
        limit: LIMIT,
        offset: intermediaryOffset,
      },
    },
    pause: isIntermediaryLastPage,
  })

  useEffect(() => {
    if (!warehouse) return

    if (warehouse.warehouseArea.list.__typename === "WarehouseAreaListFailure") {
      console.error(warehouse.warehouseArea.list.error.message)
      setIsWarehouseLastPage(true)
      return
    }

    const newLocations = (
      warehouse.warehouseArea.list.warehouseAreas.map((warehouseArea) => warehouseArea.location) as Location[]
    ).filter((location) => Boolean(location?.locationId))

    setRawLocations((prev) => [...prev, ...newLocations])

    if (newLocations.length < LIMIT) {
      setIsWarehouseLastPage(true)
    } else {
      setWarehouseOffset((prev) => prev + LIMIT)
    }
  }, [warehouse])

  useEffect(() => {
    if (!intermediaryCompaniesData) return

    if (intermediaryCompaniesData.company.list.__typename === "CompanyListFailure") {
      console.error(intermediaryCompaniesData.company.list.error.message)
      setIsIntermediaryLastPage(true)
      return
    }

    const newLocations = (
      intermediaryCompaniesData.company.list.companies.flatMap((company) => company.locations) as Location[]
    ).filter((location) => Boolean(location?.locationId))

    setRawLocations((prev) => [...prev, ...newLocations])

    if (newLocations.length < LIMIT) {
      setIsIntermediaryLastPage(true)
    } else {
      setIntermediaryOffset((prev) => prev + LIMIT)
    }
  }, [intermediaryCompaniesData])

  useEffect(() => {
    if (isWarehouseLastPage && isIntermediaryLastPage) {
      setIsFetchingLocations(false)
    }
  }, [isWarehouseLastPage, isIntermediaryLastPage])

  const locations = useMemo(() => sortIncotermsLocations(deduplicateLocations(rawLocations)), [rawLocations])

  return {
    locations,
    isFetchingLocations,
    loadMoreLocations: () => {},
  }
}
